import React, { useState, useEffect } from "react";
import AgentPopup from "./AgentPopup ";
import CreateAgentPopup from "./CreateAgentPopup";
import EditProductPopup from "./EditProductPopup ";
import ConfirmDeleteProduct from "./ConfirmDeletProduct";
import { deleteProduct } from "../../../RTK/productDetailsSlice";
import { useDispatch } from "react-redux";
import ProductAnalyticsPopup from "./ProductAnalyticsPopup";
import {
  X,
  Package,
  FileText,
  FolderOpen,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  UserPlus,
  Percent,
  Calendar,
  Users,
  Info,
  Tag,
  Globe,
  Eye,
  EyeOff,
  Clock,
  Star,
  ImageIcon,
  Search,
  Calculator,
  TrendingDown,
  BarChart3,
  Lock,
  DollarSign,
} from "lucide-react";
import { usePermissions } from "../../../contexts/PermissionsContext";
import { toast } from "react-toastify";
const AgentCard = ({ agent, index, multiplier, onAgentClick }) => {
  const finalPrice = agent.agent_price * multiplier;
  const isAvailable = agent.is_product_available;
  const isCheapest = index === 0 && isAvailable;

  return (
    <div
      onClick={() => onAgentClick(agent)}
      className={`relative p-4 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer
        ${
          isAvailable
            ? isCheapest
              ? "bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border-yellow-500/40 hover:border-yellow-500/60"
              : "bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/40 hover:border-green-500/60"
            : "bg-gradient-to-r from-red-500/20 to-pink-500/20 border-red-500/40 hover:border-red-500/60"
        }`}
    >
      {/* Badge الأرخص */}
      {isCheapest && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-xs px-3 py-1 rounded-full font-bold shadow-lg">
            الأرخص
          </div>
        </div>
      )}

      {/* Agent Info */}
      <div className="flex items-center justify-between">
        {/* اليسار - السعر والرقم */}
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold
            ${
              isCheapest
                ? "bg-yellow-500 text-black"
                : isAvailable
                  ? "bg-green-500 text-white"
                  : "bg-red-500 text-white"
            }`}
          >
            #{index + 1}
          </div>
          <div className="text-right">
            <div
              className={`font-bold text-lg ${isCheapest ? "text-yellow-400" : "text-white"}`}
            >
              ${finalPrice.toFixed(2)}
            </div>
          </div>
        </div>

        {/* الوسط - معلومات الوكيل */}
        <div className="flex-1 text-center px-4">
          <p className="text-gray-400 text-xs">ID: {agent.parent_id}</p>
        </div>

        {/* اليمين - اسم الوكيل */}
        <div className="flex items-center gap-2">
          <h3 className="text-white font-bold text-base">{agent.agent}</h3>
        </div>
      </div>

      {/* Hover Effect */}
      <div className="absolute inset-0 bg-white/5 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />
    </div>
  );
};

// Stats Card Component
const StatsCard = ({
  title,
  value,
  icon: Icon,
  color = "text-blue-400",
  bgColor = "bg-blue-500/10",
}) => (
  <div className={`${bgColor} p-4 sm:p-6 rounded-xl border border-gray-700/50`}>
    <div className="flex items-center gap-3">
      <div className={`p-2 bg-gray-800/50 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{title}</p>
        <p className={`font-bold text-lg ${color}`}>{value}</p>
      </div>
    </div>
  </div>
);

// Agents Popup Component
const AgentsPopup = ({ agents, onClose, onCreateAgent, onAgentClick }) => {
  const [multiplier, setMultiplier] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filteredAgents, setFilteredAgents] = useState([]);

  // Filter and sort
  useEffect(() => {
    if (!agents) return;

    let filtered = [...agents];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (agent) =>
          agent.agent.toLowerCase().includes(searchQuery.toLowerCase()) ||
          agent.parent_id.toString().includes(searchQuery),
      );
    }

    // Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((agent) => {
        if (filterStatus === "available") return agent.is_product_available;
        if (filterStatus === "unavailable") return !agent.is_product_available;
        return true;
      });
    }

    // Sort by price
    filtered.sort(
      (a, b) => a.agent_price * multiplier - b.agent_price * multiplier,
    );

    setFilteredAgents(filtered);
  }, [agents, searchQuery, filterStatus, multiplier]);

  // Calculate stats
  const totalAgents = agents.length;
  const availableAgents = agents.filter(
    (agent) => agent.is_product_available,
  ).length;
  const unavailableAgents = totalAgents - availableAgents;
  const cheapestPrice =
    agents.length > 0
      ? Math.min(
          ...agents
            .filter((a) => a.is_product_available)
            .map((a) => a.agent_price * multiplier),
        )
      : 0;

  return (
    <div className="fixed inset-0 z-[4000]" dir="rtl">
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="fixed inset-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Header */}
        <div className="relative bg-gray-900/90 backdrop-blur-sm border-b border-gray-700/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-[#FEC111]" />
              وكلاء المنتج
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400 hover:text-red-400" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="relative h-[calc(100%-80px)] overflow-y-auto p-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatsCard
              title="إجمالي الوكلاء"
              value={totalAgents}
              icon={Users}
              color="text-blue-400"
              bgColor="bg-blue-500/10"
            />
            <StatsCard
              title="الوكلاء المتاحين"
              value={availableAgents}
              icon={CheckCircle}
              color="text-green-400"
              bgColor="bg-green-500/10"
            />
            <StatsCard
              title="الوكلاء غير المتاحين"
              value={unavailableAgents}
              icon={XCircle}
              color="text-red-400"
              bgColor="bg-red-500/10"
            />
            <StatsCard
              title="أقل سعر"
              value={
                cheapestPrice > 0 ? `$${cheapestPrice.toFixed(2)}` : "غير محدد"
              }
              icon={TrendingDown}
              color="text-yellow-400"
              bgColor="bg-yellow-500/10"
            />
          </div>

          {/* Controls */}
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Add New Agent Button */}
              <button
                onClick={onCreateAgent}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-xl transition-all duration-300 group"
              >
                <UserPlus className="w-5 h-5 transition-transform group-hover:rotate-90" />
                <span>إضافة وكيل جديد</span>
              </button>

              {/* Search */}
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="البحث عن الوكلاء..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-700/50 text-white pr-10 pl-4 py-2.5 rounded-xl border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors placeholder-gray-400"
                />
              </div>

              {/* Filter by Status */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full bg-gray-700/50 text-white px-4 py-2.5 rounded-xl border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
              >
                <option value="all">جميع الحالات</option>
                <option value="available">متوفر فقط</option>
                <option value="unavailable">غير متوفر فقط</option>
              </select>

              {/* Multiplier */}
              <div className="relative">
                <Calculator className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  placeholder="مضاعف السعر"
                  value={multiplier}
                  onChange={(e) => setMultiplier(Number(e.target.value) || 1)}
                  className="w-full bg-gray-700/50 text-white pr-10 pl-4 py-2.5 rounded-xl border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Agents Grid */}
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
            {filteredAgents.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
                <div className="inline-block p-4 rounded-full bg-gray-800/50 mb-4">
                  <Users className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-white text-xl font-bold mb-2">
                  لا يوجد وكلاء
                </h3>
                <p className="text-gray-400 mb-6">
                  {searchQuery || filterStatus !== "all"
                    ? "لا توجد نتائج تطابق معايير البحث"
                    : "لم يتم إضافة أي وكلاء لهذا المنتج بعد"}
                </p>
                {!searchQuery && filterStatus === "all" && (
                  <button
                    onClick={onCreateAgent}
                    className="flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-xl transition-colors"
                  >
                    <UserPlus className="w-5 h-5" />
                    إضافة وكيل جديد
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredAgents.map((agent, index) => (
                  <AgentCard
                    key={agent.parent_id}
                    agent={agent}
                    index={index}
                    multiplier={multiplier}
                    onAgentClick={onAgentClick}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Info Card Component
const InfoCard = ({
  title,
  value,
  icon: Icon,
  color = "text-blue-400",
  className = "",
}) => (
  <div
    className={`bg-gradient-to-br from-gray-700/30 to-gray-800/30 p-4 sm:p-6 rounded-xl border border-gray-600/30 hover:border-gray-500/50 transition-all duration-300 hover:scale-[1.02] ${className}`}
  >
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 bg-gray-800/50 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-gray-300 font-medium text-sm">{title}</h3>
    </div>
    <p className="text-white text-base font-semibold break-all">
      {value || "غير محدد"}
    </p>
  </div>
);

// Main Popup Component
const ProductPopUp = ({ title, showPopup, setShowPopup, product }) => {
  const { hasPermission } = usePermissions(); // ✅ إضافة

  // ✅ فحص الصلاحيات
  const canCreateProduct = hasPermission("create_product");
  const canUpdateProduct = hasPermission("update_product");
  const canDeleteProduct = hasPermission("delete_product");

  const [showAgentsPopup, setShowAgentsPopup] = useState(false);
  const [showAgentPopup, setShowAgentPopup] = useState(false);
  const [showCreateAgentPopup, setShowCreateAgentPopup] = useState(false);
  const [showEditProductPopup, setShowEditProductPopup] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showProductAnalytics, setShowProductAnalytics] = useState(false);

  const dispatch = useDispatch();

  if (!showPopup) return null;

  const handleAgentClick = (agentDetails) => {
    setSelectedAgent(agentDetails);
    setShowAgentPopup(true);
    setShowAgentsPopup(true);
  };

  const handleCreateAgent = () => {
    setShowCreateAgentPopup(true);
    setShowAgentsPopup(false);
  };

  // ✅ حماية handleDeleteProduct
  const handleDeleteProduct = () => {
    if (!canDeleteProduct) {
      toast.error("ليس لديك صلاحية لحذف المنتجات 🔒");
      return;
    }
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteProduct(product.id));
    setShowConfirmDelete(false);
    setShowPopup(false);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "text-green-400 bg-green-400/10 border-green-400/20";
      case "unavailable":
        return "text-red-400 bg-red-400/10 border-red-400/20";
      default:
        return "text-gray-400 bg-gray-400/10 border-gray-400/20";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "متوفر";
      case "unavailable":
        return "غير متوفر";
      default:
        return status || "غير محدد";
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-[3000]"
        onClick={() => setShowPopup(false)}
      />

      {/* Main Modal - Full Screen Style */}
      <div
        className="fixed inset-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 z-[3500] rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden"
        dir="rtl"
      >
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Header */}
        <div className="relative bg-gray-900/90 backdrop-blur-sm border-b border-gray-700/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-7 h-7 text-[#FEC111]" />
              <div>
                <h2 className="text-xl font-bold text-white">{product.name}</h2>
                <p className="text-sm text-gray-400">تفاصيل المنتج</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Status Badge */}
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border font-medium text-sm ${getStatusColor(product.status)}`}
              >
                {product.status?.toLowerCase() === "available" ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                <span>{getStatusText(product.status)}</span>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowPopup(false)}
                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400 hover:text-red-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="relative h-[calc(100%-80px)] overflow-y-auto p-6 space-y-6">
          {/* Product Image and Basic Info */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Left Column - Image & Description */}
            <div className="xl:col-span-1 space-y-6">
              {/* Product Image */}
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-yellow-500" />
                  صورة المنتج
                </h3>
                <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-800/50">
                  {product.image && !imageError ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Package className="w-16 h-16 mb-2" />
                      <span className="text-sm">لا توجد صورة</span>
                    </div>
                  )}
                </div>
                {product.alt_text && (
                  <p className="text-gray-400 text-sm mt-3 text-center">
                    {product.alt_text}
                  </p>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-500" />
                    الوصف
                  </h3>
                  <p className="text-gray-300 leading-relaxed text-sm whitespace-pre-line">
                    {product.description}
                  </p>
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="xl:col-span-3 space-y-6">
              {/* Basic Information */}
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-500" />
                  المعلومات الأساسية
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfoCard
                    title="اسم المنتج"
                    value={product.name}
                    icon={Tag}
                    color="text-blue-400"
                  />
                  <InfoCard
                    title="معرف المنتج"
                    value={`#${product.id}`}
                    icon={Tag}
                    color="text-purple-400"
                  />
                  <InfoCard
                    title="السعر"
                    value={
                      product.price
                        ? `$${parseFloat(product.price).toFixed(4)}`
                        : "غير محدد"
                    }
                    icon={Tag}
                    color="text-yellow-400"
                  />
                  <InfoCard
                    title="نوع المنتج"
                    value={product.product_type}
                    icon={Package}
                    color="text-indigo-400"
                  />
                  <InfoCard
                    title="الوكيل الأرخص"
                    value={product.cheapest_agent_name || "غير محدد"}
                    icon={Users}
                    color="text-orange-400"
                  />
                  <InfoCard
                    title="معرف الوكيل"
                    value={
                      product.cheapest_parent_id
                        ? `ID: ${product.cheapest_parent_id}`
                        : "غير محدد"
                    }
                    icon={Tag}
                    color="text-cyan-400"
                  />
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-500" />
                  معلومات إضافية
                </h3>

                {product.is_fixed_price && (
                  <div className="mb-4 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 p-3 rounded-lg">
                    هذا المنتج يستخدم سعراً ثابتاً موحداً لجميع المستخدمين (مستثنى منه نسبه  المستويات).
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfoCard
                    title="سعر ثابت"
                    value={product.is_fixed_price ? "مفعّل" : "غير مفعّل"}
                    icon={DollarSign}
                    color={product.is_fixed_price ? "text-amber-400" : "text-gray-400"}
                  />
                  <InfoCard
                    title="التكلفة الثابتة"
                    value={
                      product.is_fixed_price && product.fixed_cost
                        ? `$${parseFloat(product.fixed_cost).toFixed(4)}`
                        : "—"
                    }
                    icon={DollarSign}
                    color="text-amber-400"
                  />
                  <InfoCard
                    title="العمولة"
                    value={
                      product.commission_rate
                        ? `${parseFloat(product.commission_rate).toFixed(2)}%`
                        : "0.00%"
                    }
                    icon={Percent}
                    color="text-green-400"
                  />
                  <InfoCard
                    title="الرؤية"
                    value={product.is_hidden ? "مخفي" : "مرئي"}
                    icon={product.is_hidden ? EyeOff : Eye}
                    color={
                      product.is_hidden ? "text-red-400" : "text-green-400"
                    }
                  />
                  <InfoCard
                    title="فوري"
                    value={product.is_instant ? "نعم" : "لا"}
                    icon={Clock}
                    color={
                      product.is_instant ? "text-green-400" : "text-yellow-400"
                    }
                  />
                  <InfoCard
                    title="متوفر"
                    value={product.available ? "نعم" : "لا"}
                    icon={product.available ? CheckCircle : XCircle}
                    color={
                      product.available ? "text-green-400" : "text-red-400"
                    }
                  />
                  <InfoCard
                    title="فحص اللاعب"
                    value={product.player_check ? "مفعل" : "غير مفعل"}
                    icon={product.player_check ? CheckCircle : XCircle}
                    color={
                      product.player_check ? "text-green-400" : "text-gray-400"
                    }
                  />
                  <InfoCard
                    title="التصنيف"
                    value={
                      product.category?.[0]?.name ||
                      product.category?.name ||
                      "غير مصنف"
                    }
                    icon={FolderOpen}
                    color="text-pink-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Product Requirements and Quantity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Requirements */}
            {product.product_requirements?.required && (
              <div className="lg:col-span-2 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                  المتطلبات المطلوبة
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.product_requirements.required.map(
                    (requirement, index) => (
                      <div
                        key={index}
                        className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 p-4 rounded-xl hover:border-blue-500/40 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-500/20 p-2 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-blue-400" />
                          </div>
                          <span className="text-white font-medium text-sm">
                            {requirement}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>

                {/* Parameters */}
                {product.params && product.params.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Info className="w-4 h-4 text-gray-400" />
                      المعاملات
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {product.params.map((param, index) => (
                        <div
                          key={index}
                          className="text-gray-300 text-sm bg-gray-700/30 p-3 rounded-lg border border-gray-600/30"
                        >
                          • {param}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quantity Values */}
            {product.qty_values && (
              <div className="lg:col-span-1 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-yellow-500" />
                  نطاق الكمية
                </h3>
                <div className="space-y-4">
                  <InfoCard
                    title="الحد الأدنى"
                    value={
                      product.qty_values.min
                        ? product.qty_values.min.toLocaleString()
                        : "غير محدد"
                    }
                    icon={CheckCircle}
                    color="text-green-400"
                  />
                  <InfoCard
                    title="الحد الأقصى"
                    value={
                      product.qty_values.max
                        ? product.qty_values.max.toLocaleString()
                        : "غير محدد"
                    }
                    icon={XCircle}
                    color="text-red-400"
                  />
                </div>
              </div>
            )}
          </div>

          {/* SEO Information */}
          {(product.seo_title || product.seo_description) && (
            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-500" />
                معلومات SEO
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {product.seo_title && (
                  <InfoCard
                    title="عنوان SEO"
                    value={product.seo_title}
                    icon={Tag}
                    color="text-purple-400"
                  />
                )}
                {product.seo_description && (
                  <InfoCard
                    title="وصف SEO"
                    value={product.seo_description}
                    icon={Info}
                    color="text-indigo-400"
                  />
                )}
              </div>
            </div>
          )}

          {/* Services and Subcategories */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Services */}
            {product.services && product.services.length > 0 && (
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  الخدمات المتاحة
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.services.map((service, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 p-4 rounded-xl hover:border-yellow-500/40 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-yellow-500/20 p-2 rounded-lg">
                          <Star className="w-4 h-4 text-yellow-400" />
                        </div>
                        <span className="text-white font-medium text-sm">
                          {service}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subcategories */}
            {product.subcategories && product.subcategories.length > 0 && (
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-blue-500" />
                  التصنيفات الفرعية
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.subcategories.map((subcategory, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 p-4 rounded-xl hover:border-blue-500/40 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-500/20 p-2 rounded-lg">
                          <FolderOpen className="w-4 h-4 text-blue-400" />
                        </div>
                        <span className="text-white font-medium text-sm">
                          {subcategory.name || subcategory}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-500" />
              إجراءات المنتج
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* ✅ زر التعديل - محمي */}
              {canUpdateProduct ? (
                <button
                  onClick={() => setShowEditProductPopup(true)}
                  className="flex items-center justify-center gap-3 px-4 py-4 bg-yellow-500/20 text-yellow-400 rounded-xl hover:bg-yellow-500/30 transition-all duration-300 font-medium border border-yellow-500/30 hover:scale-[1.02]"
                >
                  <Edit className="w-5 h-5" />
                  <span>تعديل المنتج</span>
                </button>
              ) : (
                <div className="flex items-center justify-center gap-2 px-4 py-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-400 text-sm">
                    ليس لديك صلاحية التعديل
                  </span>
                </div>
              )}

              {/* زر إدارة الوكلاء - بدون حماية (view only) */}
              <button
                onClick={() => setShowAgentsPopup(true)}
                className="flex items-center justify-center gap-3 px-4 py-4 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-all duration-300 font-medium border border-blue-500/30 hover:scale-[1.02]"
              >
                <Users className="w-5 h-5" />
                <span>إدارة الوكلاء</span>
              </button>

              {/* زر التحليلات - بدون حماية (view only) */}
              <button
                onClick={() => setShowProductAnalytics(true)}
                className="flex items-center justify-center gap-3 px-4 py-4 bg-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/30 transition-all duration-300 font-medium border border-purple-500/30 hover:scale-[1.02]"
              >
                <BarChart3 className="w-5 h-5" />
                <span>التحليلات</span>
              </button>

              {/* ✅ زر الحذف - محمي */}
              {canDeleteProduct ? (
                <button
                  onClick={handleDeleteProduct}
                  className="flex items-center justify-center gap-3 px-4 py-4 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-all duration-300 font-medium border border-red-500/30 hover:scale-[1.02]"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>حذف المنتج</span>
                </button>
              ) : (
                <div className="flex items-center justify-center gap-2 px-4 py-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-400 text-sm">
                    ليس لديك صلاحية الحذف
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Popups */}
      {showAgentsPopup && (
        <AgentsPopup
          agents={product?.agent || []}
          onClose={() => setShowAgentsPopup(false)}
          onCreateAgent={handleCreateAgent}
          onAgentClick={handleAgentClick}
        />
      )}

      {showAgentPopup && (
        <AgentPopup
          agent={selectedAgent}
          setShowAgentPopup={setShowAgentPopup}
        />
      )}

      {/* ✅ CreateAgentPopup - محمي بـ create_product */}
      {showCreateAgentPopup && canCreateProduct && (
        <CreateAgentPopup
          setShowPopup={setShowCreateAgentPopup}
          productId={product?.id}
          agents={product.agents}
          agent={product.agent}
        />
      )}

      {/* ✅ EditProductPopup - محمي بـ update_product */}
      {showEditProductPopup && canUpdateProduct && (
        <EditProductPopup
          product={product}
          setShowEditProductPopup={setShowEditProductPopup}
        />
      )}

      {/* ✅ ConfirmDeleteProduct - محمي بـ delete_product */}
      {showConfirmDelete && canDeleteProduct && (
        <ConfirmDeleteProduct
          isOpen={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showProductAnalytics && (
        <ProductAnalyticsPopup
          isOpen={showProductAnalytics}
          onClose={() => setShowProductAnalytics(false)}
          productId={product?.id}
        />
      )}
    </>
  );
};

export default ProductPopUp;
